import { Injectable, Type, inject } from '@angular/core';

import { PLATFORM, linkedTransferState } from '@frontend/vanilla/ssr';
import { BehaviorSubject, Observable, filter, firstValueFrom, map, switchMap, tap } from 'rxjs';

/**
 * @stable
 */

export enum VanillaDynamicComponentsCategory {
    Header = 'HEADER',
    PageMatrix = 'PAGE-MATRIX',
    AccountMenu = 'ACCOUNT-MENU',
    NewVisitorPage = 'NEW-VISITOR-PAGE',
    Login = 'LOGIN',
}

interface LazyComponentData {
    lazyImportFn: () => Promise<Type<any>>;
    preloading: boolean;
    preload: Promise<Type<any> | null>;
}

/**
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class DynamicComponentsRegistry {
    private platform = inject(PLATFORM);

    private readonly componentRegistrations = new Map<string, Map<string, Type<any>>>();
    private readonly lazyComponentsMap = new Map<string, Map<string, LazyComponentData>>();
    private readonly _lazyComponentRegistered = new BehaviorSubject<Map<string, Map<string, LazyComponentData>> | null>(null);

    // key -> category name, value -> component keys
    private readonly _usedComponents = linkedTransferState<Record<string, string[]>>('usedDynamicComponents', {});

    private registerComponentUsage(category: string, name: string) {
        // we want this feature to only run on the server side
        if (this.platform.isBrowser) return;

        this._usedComponents.update((x) => {
            if (!x[category])
                x[category] = [name]; // if category is not in the map, add it
            else if (!x[category].includes(name)) x[category] = [...x[category], name]; // if component is not in the category, add it
            return { ...x };
        });
    }

    get lazyComponentAdded(): Observable<Map<string, Map<string, LazyComponentData>> | null> {
        return this._lazyComponentRegistered;
    }

    registerLazyComponent(category: string, name: string, lazyImportFn: () => Promise<Type<any>>) {
        let categoryMap = this.lazyComponentsMap.get(category);
        if (!categoryMap) {
            categoryMap = new Map<string, LazyComponentData>();
            this.lazyComponentsMap.set(category, categoryMap);
        }

        categoryMap.set(name, {
            lazyImportFn,
            preloading: false,
            preload: Promise.resolve(null),
        });

        this.platform.runOnBrowser(() => {
            // when a component is registered and we know it's going to be used immediately on the page
            // we want to preload it immediately
            if (this._usedComponents()[category]?.includes(name)) {
                this.preloadLazyComponent(category, name);
            }
        });

        this._lazyComponentRegistered.next(this.lazyComponentsMap);
    }

    preloadLazyComponent(category: string, name: string) {
        const categoryMap = this.lazyComponentsMap.get(category);
        if (categoryMap) {
            const lazyCmpData = categoryMap.get(name);
            if (lazyCmpData) {
                categoryMap.set(name, {
                    ...lazyCmpData,
                    preloading: true,
                    preload: lazyCmpData.lazyImportFn(),
                });
            }
        }
    }

    /*Returns import function for lazy components in a reactive way.
     * Ensures that late component registrations are also considered.
     */
    getLazyComponentReactive(category: string, name: string): Promise<Type<any> | null> {
        return firstValueFrom(
            this.lazyComponentAdded.pipe(
                map((componentsMap) => componentsMap?.get(category)?.get(name)),
                filter(Boolean),
                switchMap(({ lazyImportFn, preload, preloading }) => (preloading ? preload : lazyImportFn())),
                tap(() => this.registerComponentUsage(category, name)),
            ),
        );
    }

    getLazyComponent(category: string, name: string): Promise<Type<any> | null> {
        const categoryMap = this.lazyComponentsMap.get(category);
        if (categoryMap) {
            const lazyCmpData = categoryMap.get(name);
            if (lazyCmpData) {
                this.registerComponentUsage(category, name);

                const { lazyImportFn, preload, preloading } = lazyCmpData;
                return preloading ? preload : lazyImportFn();
            }
        }
        return Promise.resolve(null);
    }

    registerComponent(category: string, name: string, componentType: Type<any>) {
        let categoryMap = this.componentRegistrations.get(category);
        if (!categoryMap) {
            categoryMap = new Map();
            this.componentRegistrations.set(category, categoryMap);
        }

        categoryMap.set(name, componentType);
    }

    get(category: string, name: string): Type<any> | null {
        const categoryMap = this.componentRegistrations.get(category);
        if (categoryMap) {
            return categoryMap.get(name) || null;
        }

        return null;
    }
}
